import React, { useState, useEffect, useCallback } from 'react';
import GameBoard from './GameBoard';
import GameInfoPanel from './GameInfoPanel';
import InstructionsPanel from './InstructionsPanel';
import Controls from './Controls';
import {
    UNIT_TYPES, GRID_WIDTH, GRID_HEIGHT, CENTER_COL, CENTER_ROW, CENTER_HEX_ID,
    OWNER_PLAYER, OWNER_COMPUTER, OWNER_NATURE,
    STATUS_PLAYING, STATUS_PLAYER_WON, STATUS_COMPUTER_WON,
    ACTION_MOVE, ACTION_ATTACK,
    EXPLOSION_DURATION, AI_THINKING_TIME, POST_AI_ACTION_DELAY, POST_AI_NO_ACTION_DELAY
} from './constants';
import { getNeighbors, calculateHexesInRange, generateBushClump } from './utils';

const WiggiWiggiGame = () => {
    // --- State ---
    const [gameUnits, setGameUnits] = useState({});
    const [selectedHex, setSelectedHex] = useState(null);
    const [possibleMoves, setPossibleMoves] = useState([]);
    const [possibleAttacks, setPossibleAttacks] = useState([]);
    const [clearedBushes, setClearedBushes] = useState({});
    const [bushHexes, setBushHexes] = useState(new Set());
    const [visibilityData, setVisibilityData] = useState({ allVisible: new Set(), seenByBomber: new Set() });
    const [currentTurn, setCurrentTurn] = useState(OWNER_PLAYER);
    const [gameStatus, setGameStatus] = useState(STATUS_PLAYING);
    const [message, setMessage] = useState("Player's turn - Select a unit");
    const [gameTurn, setGameTurn] = useState(1);

    // --- Fog of War Calculation ---
    // useCallback might be useful if passed as prop, but defined here it's fine
    const calculateVisibleHexes = useCallback((currentUnits, viewingOwner) => {
        const allVisible = new Set();
        const seenByBomber = new Set();
        const friendlyUnitLocations = [];
        const enemyUnitHexIds = new Set();

        // Pass 1: Standard sight & unit locations
        for (const hexId in currentUnits) {
            const unit = currentUnits[hexId];
            const [col, row] = hexId.split(',').map(Number);

            if (unit.owner === viewingOwner) {
                friendlyUnitLocations.push({ col, row, hexId });
                const unitStats = UNIT_TYPES[unit.type];
                let sightRange = unitStats?.sightRange ?? 0;

                if (unit.type === 'COCONUT_BOMBER') {
                     if (unit.isActivated) {
                         const lifespan = unit.lifespanRemaining;
                         if (lifespan >= 10) sightRange = 4;
                         else if (lifespan >= 6) sightRange = 3;
                         else if (lifespan >= 2) sightRange = 2;
                         else if (lifespan === 1) sightRange = 1;
                         else sightRange = 0;
                     } else {
                         sightRange = 1; // Unactivated bomber base sight
                     }
                }

                if (sightRange > 0) {
                    const newlyVisible = calculateHexesInRange(col, row, sightRange);
                    newlyVisible.forEach(id => allVisible.add(id));
                    if (unit.type === 'COCONUT_BOMBER') {
                        newlyVisible.forEach(id => seenByBomber.add(id));
                    }
                } else {
                    allVisible.add(hexId); // Unit sees its own hex
                }
            } else if (unit.owner !== OWNER_NATURE) {
                enemyUnitHexIds.add(hexId);
            } else if (unit.type === 'IDOL_STONE') {
                 allVisible.add(hexId); // Ensure Idols hexes are always "visible"
            }
        }

        // Pass 2: Proximity detection rule
        const PROXIMITY_DETECTION_RANGE = 2;
        for (const friendlyUnit of friendlyUnitLocations) {
            const nearbyHexes = calculateHexesInRange(friendlyUnit.col, friendlyUnit.row, PROXIMITY_DETECTION_RANGE);
            for (const nearbyHexId of nearbyHexes) {
                if (enemyUnitHexIds.has(nearbyHexId)) {
                    allVisible.add(nearbyHexId);
                }
            }
        }
        return { allVisible, seenByBomber };
    }, []); // Dependencies: None, relies on arguments and constants


    // --- Game Initialization ---
    const initializeGame = useCallback(() => {
        console.log("Initializing game...");
        const initialUnits = {};
        const initialBushSet = new Set();
        const centralBushRow = Math.floor(GRID_HEIGHT / 2);

        const neighborHexIds = getNeighbors(CENTER_COL, CENTER_ROW); // Uses utils version
        const idolHexIds = new Set([CENTER_HEX_ID, ...neighborHexIds]);
        idolHexIds.forEach(hexId => {
            initialUnits[hexId] = { id: `idol_${hexId}`, owner: OWNER_NATURE, type: 'IDOL_STONE', health: UNIT_TYPES.IDOL_STONE.health };
        });

        // Central bushes
        for (let c = 0; c < GRID_WIDTH; c++) {
            const hexId = `${c},${centralBushRow}`;
            if (!idolHexIds.has(hexId)) {
                initialBushSet.add(hexId);
            }
        }

        // Random bush clumps
        const playerRows = [8, 9, 10];
        const computerRows = [2, 3, 4];
        const allGeneratedClumpHexes = new Set();
        for (let i = 0; i < 2; i++) { // Player clumps
             const currentBushes = new Set([...initialBushSet, ...allGeneratedClumpHexes]);
             const newClump = generateBushClump(playerRows, currentBushes, idolHexIds, GRID_WIDTH, GRID_HEIGHT);
             newClump.forEach(hexId => { initialBushSet.add(hexId); allGeneratedClumpHexes.add(hexId); });
        }
         for (let i = 0; i < 2; i++) { // Computer clumps
             const currentBushes = new Set([...initialBushSet, ...allGeneratedClumpHexes]);
             const newClump = generateBushClump(computerRows, currentBushes, idolHexIds, GRID_WIDTH, GRID_HEIGHT);
             newClump.forEach(hexId => { initialBushSet.add(hexId); allGeneratedClumpHexes.add(hexId); });
         }


        // Helper to place units
        const placeUnit = (hexId, unitData) => {
            if (!initialUnits[hexId] && !initialBushSet.has(hexId)) {
                initialUnits[hexId] = unitData; return true;
            }
            console.warn(`Could not place unit ${unitData.id || unitData.type} at ${hexId}, already occupied or bush.`);
            return false;
        };

          // Place Player Units
        placeUnit(`10,${GRID_HEIGHT - 1}`, { id: 'pb', owner: 'PLAYER', type: 'BANANA_TREE', health: UNIT_TYPES.BANANA_TREE.health });
        placeUnit(`1,${GRID_HEIGHT - 2}`, { id: 'p1', owner: 'PLAYER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });
        placeUnit(`3,${GRID_HEIGHT - 2}`, { id: 'p2', owner: 'PLAYER', type: 'BUSHWHACKER', health: UNIT_TYPES.BUSHWHACKER.health });
        placeUnit(`5,${GRID_HEIGHT - 2}`, { id: 'p3', owner: 'PLAYER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });
        placeUnit(`7,${GRID_HEIGHT - 2}`, { id: 'p4', owner: 'PLAYER', type: 'EXPLODING_COCONUT_RUNNER', health: UNIT_TYPES.EXPLODING_COCONUT_RUNNER.health });
        placeUnit(`9,${GRID_HEIGHT - 2}`, { id: 'p5', owner: 'PLAYER', type: 'COCONUT_BOMBER', lifespanRemaining: 11, isActivated: false, activationTurn: null, health: UNIT_TYPES.COCONUT_BOMBER.health });
        placeUnit(`11,${GRID_HEIGHT - 2}`, { id: 'p6', owner: 'PLAYER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });
        placeUnit(`13,${GRID_HEIGHT - 2}`, { id: 'p7', owner: 'PLAYER', type: 'BUSHWHACKER', health: UNIT_TYPES.BUSHWHACKER.health });
        placeUnit(`15,${GRID_HEIGHT - 2}`, { id: 'p8', owner: 'PLAYER', type: 'EXPLODING_COCONUT_RUNNER', health: UNIT_TYPES.EXPLODING_COCONUT_RUNNER.health });
        placeUnit(`17,${GRID_HEIGHT - 2}`, { id: 'p9', owner: 'PLAYER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });
        placeUnit(`19,${GRID_HEIGHT - 2}`, { id: 'p10', owner: 'PLAYER', type: 'BUSHWHACKER', health: UNIT_TYPES.BUSHWHACKER.health });
        placeUnit(`8,${GRID_HEIGHT - 1}`, { id: 'p11', owner: 'PLAYER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });

        // Place Computer Units
        placeUnit(`10,0`, { id: 'cb', owner: 'COMPUTER', type: 'BANANA_TREE', health: UNIT_TYPES.BANANA_TREE.health });
        placeUnit(`1,1`, { id: 'c1', owner: 'COMPUTER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });
        placeUnit(`3,1`, { id: 'c2', owner: 'COMPUTER', type: 'BUSHWHACKER', health: UNIT_TYPES.BUSHWHACKER.health });
        placeUnit(`5,1`, { id: 'c3', owner: 'COMPUTER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });
        placeUnit(`7,1`, { id: 'c4', owner: 'COMPUTER', type: 'EXPLODING_COCONUT_RUNNER', health: UNIT_TYPES.EXPLODING_COCONUT_RUNNER.health });
        placeUnit(`9,1`, { id: 'c5', owner: 'COMPUTER', type: 'COCONUT_BOMBER', lifespanRemaining: 11, isActivated: false, activationTurn: null, health: UNIT_TYPES.COCONUT_BOMBER.health });
        placeUnit(`11,1`, { id: 'c6', owner: 'COMPUTER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });
        placeUnit(`13,1`, { id: 'c7', owner: 'COMPUTER', type: 'BUSHWHACKER', health: UNIT_TYPES.BUSHWHACKER.health });
        placeUnit(`15,1`, { id: 'c8', owner: 'COMPUTER', type: 'EXPLODING_COCONUT_RUNNER', health: UNIT_TYPES.EXPLODING_COCONUT_RUNNER.health });
        placeUnit(`17,1`, { id: 'c9', owner: 'COMPUTER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });
        placeUnit(`19,1`, { id: 'c10', owner: 'COMPUTER', type: 'BUSHWHACKER', health: UNIT_TYPES.BUSHWHACKER.health });
        placeUnit(`8,0`, { id: 'c11', owner: 'COMPUTER', type: 'COCONUT_CLUBBER', health: UNIT_TYPES.COCONUT_CLUBBER.health });


        setGameUnits(initialUnits);
        setBushHexes(initialBushSet);
        setClearedBushes({});
        setSelectedHex(null);
        setPossibleMoves([]);
        setPossibleAttacks([]);
        setCurrentTurn(OWNER_PLAYER);
        setGameStatus(STATUS_PLAYING);
        setMessage("Player's turn - Select a unit");
        setGameTurn(1);
        setVisibilityData(calculateVisibleHexes(initialUnits, OWNER_PLAYER));
    }, [calculateVisibleHexes]); // Add calculateVisibleHexes dependency

    // Run initialization on mount
    useEffect(() => {
        initializeGame();
    }, [initializeGame]);


     // --- Action Calculations --- (Keep these within WiggiWiggiGame as they depend heavily on state)
     const calculatePossibleMoves = useCallback((col, row, moveRange, unitType) => {
         // (Keep the original logic from WiggiWiggiGame here)
         // ... Uses getNeighbors, gameUnits, bushHexes, clearedBushes, UNIT_TYPES
         // Replace console.log/warn with appropriate feedback or logging strategy
         const moves = [];
         const visited = new Set();
         const queue = [{ col, row, distance: 0 }];
         const startKey = `${col},${row}`;
         visited.add(startKey);

         const unitOwner = gameUnits[startKey]?.owner;
         const unitStats = UNIT_TYPES[unitType];
         if (!unitStats) return [];

         const canClearBushesFlag = unitStats.canClearBushes; // Renamed to avoid conflict
         const isRunner = unitType === 'EXPLODING_COCONUT_RUNNER';
         const isBomber = unitType === 'COCONUT_BOMBER';

         while (queue.length > 0) {
             const { col: c, row: r, distance } = queue.shift();
             if (distance < moveRange) {
                 const neighbors = getNeighbors(c, r); // Use util
                 for (const neighbor of neighbors) {
                     if (!visited.has(neighbor)) {
                         visited.add(neighbor);
                         const [nc, nr] = neighbor.split(',').map(Number);
                         const isNeighborBush = bushHexes.has(neighbor) && !clearedBushes[neighbor];
                         const unitOnNeighbor = gameUnits[neighbor];
                         const isOccupied = !!unitOnNeighbor;
                         let canMoveHere = false;
                         let isFinalStep = false; // Cannot continue pathfinding *from* this hex

                         if (isBomber) {
                             // Bombers fly over everything except other units and Idols
                             if (!isOccupied || (isOccupied && unitOnNeighbor.type === 'IDOL_STONE')) {
                                 canMoveHere = !isOccupied; // Can move if empty
                             } else {
                                 canMoveHere = false; // Cannot move onto existing unit
                             }
                              isFinalStep = !canMoveHere; // Stop BFS if cannot land here
                         } else { // Non-bombers
                             if (isOccupied) {
                                 if (unitOnNeighbor.type === 'BANANA_TREE' && unitOnNeighbor.owner !== unitOwner && unitOnNeighbor.owner !== OWNER_NATURE) {
                                     canMoveHere = true; isFinalStep = true; // Capture Tree
                                 } else if (isRunner && unitOnNeighbor.type !== 'IDOL_STONE' && unitOnNeighbor.type !== 'BANANA_TREE') {
                                     canMoveHere = true; isFinalStep = true; // Explode on unit
                                 } else {
                                     canMoveHere = false; // Blocked by friendly/idol/non-target enemy
                                 }
                             } else { // Not occupied
                                 if (!isNeighborBush || (isNeighborBush && canClearBushesFlag)) {
                                     canMoveHere = true; isFinalStep = false; // Move to empty/clearable bush
                                 } else {
                                      canMoveHere = false; // Blocked by uncleared bush
                                 }
                             }
                         }

                         if (canMoveHere) {
                             moves.push(neighbor);
                             if (!isFinalStep) {
                                 queue.push({ col: nc, row: nr, distance: distance + 1 });
                             }
                         }
                     }
                 }
             }
         }
         return moves;
     }, [gameUnits, bushHexes, clearedBushes]); // Dependencies for move calculation


      const calculatePossibleAttacks = useCallback((col, row, attackRange, attackerOwner) => {
          // (Keep the original logic from WiggiWiggiGame here)
          // ... Uses getNeighbors, gameUnits
          // Replace console.log/warn with appropriate feedback or logging strategy
         const attacks = [];
         const visited = new Set();
         const queue = [{ col, row, distance: 0 }];
         const startKey = `${col},${row}`;
         visited.add(startKey);

         while (queue.length > 0) {
             const { col: c, row: r, distance } = queue.shift();
             if (distance < attackRange) {
                 const neighbors = getNeighbors(c, r); // Use util
                 for (const neighbor of neighbors) {
                     if (!visited.has(neighbor)) {
                         visited.add(neighbor);
                         const targetUnit = gameUnits[neighbor];
                         if (targetUnit && targetUnit.owner !== attackerOwner && targetUnit.owner !== OWNER_NATURE) {
                             attacks.push(neighbor); // Found a valid target
                         }
                         // Continue BFS up to attack range, even if target found here
                         const [nc, nr] = neighbor.split(',').map(Number);
                         queue.push({ col: nc, row: nr, distance: distance + 1 });
                     }
                 }
             }
         }
         return attacks;
      }, [gameUnits]); // Dependency for attack calculation


    // --- Win Condition Check ---
    // Use useCallback as it's passed to action handlers
     const checkWinCondition = useCallback((currentUnits) => {
         if (gameStatus !== STATUS_PLAYING) return false; // Don't check if already decided

         let computerHasCombatUnits = false;
         let playerHasCombatUnits = false;
         let computerHasBanana = false;
         let playerHasBanana = false;

         for (const hexId in currentUnits) {
             const unit = currentUnits[hexId];
             if (unit.owner === OWNER_COMPUTER) {
                 if (unit.type === 'BANANA_TREE') computerHasBanana = true;
                 else if (unit.type !== 'IDOL_STONE') computerHasCombatUnits = true;
             } else if (unit.owner === OWNER_PLAYER) {
                 if (unit.type === 'BANANA_TREE') playerHasBanana = true;
                 else if (unit.type !== 'IDOL_STONE') playerHasCombatUnits = true;
             }
         }

          let statusChanged = false;
         // Win by eliminating all enemy combat units (if player still has theirs)
         if (playerHasBanana && !computerHasCombatUnits && computerHasBanana) {
             setGameStatus(STATUS_PLAYER_WON);
             setMessage('Victory! You eliminated all enemy combat units!');
             statusChanged = true;
         }
         // Lose if player has no combat units left (if computer still has theirs)
         else if (computerHasBanana && !playerHasCombatUnits && playerHasBanana) {
             setGameStatus(STATUS_COMPUTER_WON);
             setMessage('Defeat! The computer eliminated all your combat units!');
             statusChanged = true;
         }
         // Banana Tree captures handled directly in move/attack

         return statusChanged; // Return true if the status was changed

     }, [gameStatus]); // Dependency: gameStatus (to avoid checks when game is over)


    // --- End of Turn Cleanup (Bombers) ---
     // Use useCallback as it's called within action handlers
     const handleEndOfTurnCleanup = useCallback((ownerWhoseTurnEnded, currentUnitsState) => {
         const nextUnitsState = JSON.parse(JSON.stringify(currentUnitsState)); // Deep copy
         const unitsToRemove = [];
         let bomberCrashMessage = "";

         for (const hexId in nextUnitsState) {
             const unit = nextUnitsState[hexId];
             if (unit.type === 'COCONUT_BOMBER' && unit.owner === ownerWhoseTurnEnded && unit.isActivated) {
                 const newLifespan = (unit.lifespanRemaining || 0) - 1;
                 nextUnitsState[hexId].lifespanRemaining = newLifespan;
                 if (newLifespan <= 0) {
                     console.log(`[BOMBER] Bomber ${unit.id || hexId} (${unit.owner}) crashed!`);
                     unitsToRemove.push(hexId);
                     bomberCrashMessage += ` A ${unit.owner} Coconut Bomber crashed!`;
                 }
             }
         }

         if (unitsToRemove.length > 0) {
             unitsToRemove.forEach(hexId => { delete nextUnitsState[hexId]; });
             // Use functional update for message to append safely
             setMessage(prev => prev + bomberCrashMessage);
         }

         return nextUnitsState; // Return potentially modified state
     }, []); // No dependencies needed if it only reads constants and args


    // --- AI Turn Logic ---
    // Needs access to many state setters and calculation functions
    const executeAITurn = useCallback((currentUnitsState) => {
        if (gameStatus !== STATUS_PLAYING) return; // Check status again inside timeout

        let stateAfterAIAction = JSON.parse(JSON.stringify(currentUnitsState));
        let actionTaken = false;
        let aiMessage = "Computer ponders..."; // Default message

        const computerUnits = [];
        for (const hexId in stateAfterAIAction) {
            const unit = stateAfterAIAction[hexId];
            if (unit.owner === OWNER_COMPUTER && unit.type !== 'BANANA_TREE' && unit.type !== 'IDOL_STONE') {
                const [col, row] = hexId.split(',').map(Number);
                computerUnits.push({ hexId, col, row, ...unit });
            }
        }

        if (computerUnits.length > 0) {
            const allPossibleActions = [];
            for (const unit of computerUnits) {
                const unitStats = UNIT_TYPES[unit.type];
                if (!unitStats) continue;

                if (unitStats.moveRange > 0) {
                    const moves = calculatePossibleMoves(unit.col, unit.row, unitStats.moveRange, unit.type);
                    moves.forEach(toHexId => allPossibleActions.push({ type: ACTION_MOVE, unitHexId: unit.hexId, targetHexId: toHexId }));
                }
                if (unitStats.attackRange > 0) {
                    const attacks = calculatePossibleAttacks(unit.col, unit.row, unitStats.attackRange, OWNER_COMPUTER);
                    attacks.forEach(targetHexId => allPossibleActions.push({ type: ACTION_ATTACK, unitHexId: unit.hexId, targetHexId: targetHexId }));
                }
            }

            if (allPossibleActions.length > 0) {
                const randomAction = allPossibleActions[Math.floor(Math.random() * allPossibleActions.length)];
                const unitToAct = stateAfterAIAction[randomAction.unitHexId]; // Get fresh unit data from current state copy
                 const targetUnitInfo = stateAfterAIAction[randomAction.targetHexId]; // Get target info

                if (!unitToAct) {
                     console.error("AI Error: Unit to act not found in state:", randomAction.unitHexId);
                 }
                 else if (randomAction.type === ACTION_MOVE) {
                    actionTaken = true; // Assume action happens unless error

                     // Handle AI capturing player's Banana Tree
                    if (targetUnitInfo && targetUnitInfo.type === 'BANANA_TREE' && targetUnitInfo.owner === OWNER_PLAYER) {
                         stateAfterAIAction[randomAction.targetHexId] = unitToAct;
                         delete stateAfterAIAction[randomAction.unitHexId];
                         setGameUnits(stateAfterAIAction); // Update state immediately for win
                         setGameStatus(STATUS_COMPUTER_WON);
                         setMessage('Defeat! The computer captured your Sacred Banana Tree!');
                         return; // End AI turn processing here
                    }
                    // Handle AI EXPLODING COCONUT RUNNER Collision
                    else if (unitToAct.type === 'EXPLODING_COCONUT_RUNNER' && targetUnitInfo && targetUnitInfo.type !== 'IDOL_STONE' && targetUnitInfo.type !== 'BANANA_TREE') {
                         const runnerName = UNIT_TYPES[unitToAct.type].name;
                         const targetName = UNIT_TYPES[targetUnitInfo.type]?.name || 'Unknown';
                         aiMessage = `Computer's ${runnerName} exploded on contact with your ${targetName}!`;
                         setMessage(aiMessage); // Set message immediately

                         // Mark for explosion visual in main state
                         setGameUnits(current => {
                             const explodingState = JSON.parse(JSON.stringify(current));
                             if (explodingState[randomAction.unitHexId]) explodingState[randomAction.unitHexId].exploding = 'runner';
                             if (explodingState[randomAction.targetHexId]) explodingState[randomAction.targetHexId].exploding = 'target';
                             return explodingState;
                         });

                         // Schedule cleanup and turn transition after delay
                         setTimeout(() => {
                            // Update state functionally to get latest exploding state
                            setGameUnits(currentExplodingUnits => {
                                 const finalState = JSON.parse(JSON.stringify(currentExplodingUnits));
                                 delete finalState[randomAction.unitHexId]; // Remove units
                                 delete finalState[randomAction.targetHexId];
                                 // Pass the state *without* the exploded units to cleanup and win check
                                 const unitsAfterCleanup = handleEndOfTurnCleanup(OWNER_COMPUTER, finalState);
                                 const winStatusChanged = checkWinCondition(unitsAfterCleanup);
                                 // Must check gameStatus *after* checkWinCondition potentially updated it
                                 // Need to read updated gameStatus state here, which is tricky with closures/timeouts.
                                 // A better approach might be to have checkWinCondition return the new status,
                                 // or use a ref to track the latest status.
                                 // For simplicity now, we assume checkWinCondition sets state synchronously enough for the next step.
                                 // Let's read it via a state getter function if possible, or just proceed.

                                 // If game didn't end, proceed to next player turn
                                 // We read gameStatus *before* setGameUnits finishes, this is risky.
                                 // Let's assume checkWinCondition already set the final status
                                 if (!winStatusChanged) { // If checkWin didn't end the game
                                      const nextTurn = gameTurn + 1;
                                      setGameTurn(nextTurn);
                                      const nextVisibility = calculateVisibleHexes(unitsAfterCleanup, OWNER_PLAYER);
                                      setVisibilityData(nextVisibility);
                                      setTimeout(() => {
                                            setCurrentTurn(OWNER_PLAYER);
                                            setMessage(`Player's turn (${nextTurn}) - Select a unit`);
                                      }, POST_AI_ACTION_DELAY);
                                 }
                                 return unitsAfterCleanup; // Set the final state
                            });
                         }, EXPLOSION_DURATION);
                         return; // Prevent standard AI cleanup/turn end for explosion
                    }
                    // Handle standard AI movement / bush clearing
                    else {
                         stateAfterAIAction[randomAction.targetHexId] = { ...unitToAct }; // Move unit
                         delete stateAfterAIAction[randomAction.unitHexId];
                         const isBush = bushHexes.has(randomAction.targetHexId) && !clearedBushes[randomAction.targetHexId];
                         const unitStats = UNIT_TYPES[unitToAct.type];
                         aiMessage = `Computer moved ${unitStats.name}.`;

                         // Bush clearing
                         if (isBush && unitStats.canClearBushes) {
                             // Must update clearedBushes state directly here for AI logic
                             setClearedBushes(prev => ({ ...prev, [randomAction.targetHexId]: true }));
                              aiMessage = `Computer's ${unitStats.name} cleared a bush.`;
                              // Note: stateAfterAIAction doesn't reflect this change yet, but the main state does.
                              // This is a bit inconsistent. Maybe pass clearedBushes setter down? Or handle in main update.
                              // For now, message is updated, state update happens later.
                         }
                         // Bomber activation
                          if (unitToAct.type === 'COCONUT_BOMBER' && !unitToAct.isActivated) {
                               stateAfterAIAction[randomAction.targetHexId].isActivated = true;
                               stateAfterAIAction[randomAction.targetHexId].activationTurn = gameTurn;
                               console.log(`[BOMBER] AI Bomber ${unitToAct.id || randomAction.unitHexId} activated on turn ${gameTurn}!`);
                           }

                         setMessage(aiMessage);
                    }
                 }
                 else if (randomAction.type === ACTION_ATTACK) {
                    actionTaken = true; // Assume action happens

                    // AI attacking player's Banana Tree (Capture)
                    if (targetUnitInfo && targetUnitInfo.type === 'BANANA_TREE' && targetUnitInfo.owner === OWNER_PLAYER) {
                         stateAfterAIAction[randomAction.targetHexId] = unitToAct;
                         delete stateAfterAIAction[randomAction.unitHexId];
                         setGameUnits(stateAfterAIAction);
                         setGameStatus(STATUS_COMPUTER_WON);
                         setMessage('Defeat! The computer captured your Sacred Banana Tree!');
                         return; // End AI turn
                    }
                     // Standard attack
                     else if (targetUnitInfo) { // Ensure target still exists
                         const attackerStats = UNIT_TYPES[unitToAct.type];
                         const targetStats = UNIT_TYPES[targetUnitInfo.type];
                         const damage = Math.max(0, attackerStats.attack || 0);
                         const newTargetHealth = Math.max(0, targetUnitInfo.health - damage);

                         if (newTargetHealth <= 0) {
                             aiMessage = `Computer's ${attackerStats.name} defeated your ${targetStats.name}!`;
                              // Attacker moves into the defeated unit's hex
                              stateAfterAIAction[randomAction.targetHexId] = { ...unitToAct }; // Move attacker
                              delete stateAfterAIAction[randomAction.unitHexId]; // Remove attacker from old hex
                         } else {
                             stateAfterAIAction[randomAction.targetHexId].health = newTargetHealth; // Update health
                              aiMessage = `Computer's ${attackerStats.name} dealt ${damage} damage to your ${targetStats.name}.`;
                         }
                         setMessage(aiMessage);
                     } else {
                        // Target might have been destroyed by previous action? Skip or log.
                        actionTaken = false;
                        console.warn("AI Attack target missing:", randomAction.targetHexId);
                     }
                }
            } else { // No possible actions
                 aiMessage = "Computer has no available actions.";
                 actionTaken = false;
                 setMessage(aiMessage);
            }
        } else { // No computer units left (should be handled by win condition?)
            aiMessage = "Computer has no units to move.";
             actionTaken = false;
             setMessage(aiMessage);
        }

        // --- AI Turn Cleanup & State Update (for NON-EXPLOSION actions) ---
        const finalUnitsState = handleEndOfTurnCleanup(OWNER_COMPUTER, stateAfterAIAction);
        setGameUnits(finalUnitsState); // Update state with result of AI action & cleanup
        const winStatusChanged = checkWinCondition(finalUnitsState); // Check win after AI move

        // If game didn't end, proceed to next player turn
         if (!winStatusChanged && gameStatus === STATUS_PLAYING) { // Double check status
             const nextTurn = gameTurn + 1;
             setGameTurn(nextTurn);
             const nextVisibility = calculateVisibleHexes(finalUnitsState, OWNER_PLAYER);
             setVisibilityData(nextVisibility);

             setTimeout(() => {
                 setCurrentTurn(OWNER_PLAYER);
                 setMessage(`Player's turn (${nextTurn}) - Select a unit`);
             }, actionTaken ? POST_AI_ACTION_DELAY : POST_AI_NO_ACTION_DELAY);
         }

    }, [
        gameStatus, gameTurn, bushHexes, clearedBushes, // State reads
        calculatePossibleMoves, calculatePossibleAttacks, handleEndOfTurnCleanup, checkWinCondition, calculateVisibleHexes, // Util functions
        setGameUnits, setGameStatus, setMessage, setGameTurn, setCurrentTurn, setVisibilityData, setClearedBushes // State setters
    ]);


     // --- Player Action: End Turn / Start AI ---
     // Use useCallback as it's called within action handlers
      const endPlayerTurn = useCallback((unitsAfterPlayerAction) => {
          if (gameStatus !== STATUS_PLAYING) return; // Don't proceed if game ended

          setCurrentTurn(OWNER_COMPUTER);
          setMessage("Computer's turn...");

          // Use timeout for AI 'thinking' delay
           setTimeout(() => executeAITurn(unitsAfterPlayerAction), AI_THINKING_TIME);

      }, [gameStatus, executeAITurn]); // Depends on gameStatus and the AI execution function


     // --- Player Action: Move Unit ---
     // Use useCallback as it's passed as prop or used in handleHexClick
     const moveUnit = useCallback((fromHexId, toHexId) => {
         const unit = gameUnits[fromHexId];
         if (!unit) {
             console.error("Move failed: No unit found at source hex", fromHexId);
             setMessage("Error: Selected unit not found. Please select again.");
             setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]);
             return;
         }

         const targetUnitInfo = gameUnits[toHexId];
         const unitStats = UNIT_TYPES[unit.type];

         // --- EXPLODING COCONUT RUNNER Collision ---
         if (unit.type === 'EXPLODING_COCONUT_RUNNER' && targetUnitInfo && targetUnitInfo.type !== 'IDOL_STONE' && targetUnitInfo.type !== 'BANANA_TREE') {
             const runnerName = unitStats.name;
             const targetName = UNIT_TYPES[targetUnitInfo.type]?.name || 'Unknown';
             const explosionMsg = `${runnerName} (${unit.owner}) exploded on contact with ${targetName} (${targetUnitInfo.owner})!`;
             setMessage(explosionMsg);

              // Mark units for explosion visual
              setGameUnits(current => {
                 const explodingState = JSON.parse(JSON.stringify(current));
                  if (explodingState[fromHexId]) explodingState[fromHexId].exploding = 'runner';
                  if (explodingState[toHexId]) explodingState[toHexId].exploding = 'target';
                  return explodingState;
              });
             setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]);

             // Cleanup after delay
             setTimeout(() => {
                  setGameUnits(currentExplodingUnits => {
                     const finalState = JSON.parse(JSON.stringify(currentExplodingUnits));
                     delete finalState[fromHexId]; delete finalState[toHexId];
                     // Pass state *without* exploded units to cleanup/win check
                     const unitsAfterCleanup = handleEndOfTurnCleanup(OWNER_PLAYER, finalState);
                     const winStatusChanged = checkWinCondition(unitsAfterCleanup);
                     // Read gameStatus after checkWinCondition potentially updated it (same caveat as AI explosion)
                      if (!winStatusChanged) { // If game didn't end
                          endPlayerTurn(unitsAfterCleanup); // Start AI turn with the cleaned-up state
                      }
                     return unitsAfterCleanup; // Set the final state
                 });
             }, EXPLOSION_DURATION);
             return; // Stop further move processing
         }

         // --- Capture Enemy Banana Tree ---
         else if (targetUnitInfo && targetUnitInfo.type === 'BANANA_TREE' && targetUnitInfo.owner === OWNER_COMPUTER) {
             const finalUnits = JSON.parse(JSON.stringify(gameUnits));
             finalUnits[toHexId] = unit; delete finalUnits[fromHexId];
             setGameUnits(finalUnits);
             setGameStatus(STATUS_PLAYER_WON);
             setMessage('Victory! You captured the enemy Sacred Banana Tree!');
             setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]);
             return; // Stop further move processing
         }

         // --- Standard Move & Bush Clearing ---
         else {
             const isBush = bushHexes.has(toHexId) && !clearedBushes[toHexId];
             let moveMessage = `${unitStats.name} moved.`;
             const nextGameUnits = JSON.parse(JSON.stringify(gameUnits));
             nextGameUnits[toHexId] = { ...unit }; delete nextGameUnits[fromHexId];

             // Bush Clearing
             if (isBush && unitStats.canClearBushes) {
                  // Update clearedBushes state immediately
                  setClearedBushes(prev => ({ ...prev, [toHexId]: true }));
                  moveMessage = `${unitStats.name} cleared a bush!`;
                   // Note: nextGameUnits doesn't reflect this, but main state does.
             }

              // Bomber Activation
              if (unit.type === 'COCONUT_BOMBER' && !unit.isActivated) {
                   nextGameUnits[toHexId].isActivated = true;
                   nextGameUnits[toHexId].activationTurn = gameTurn;
                   console.log(`[BOMBER] Player Bomber ${unit.id || fromHexId} activated on turn ${gameTurn}!`);
               }


             setMessage(moveMessage);
             // Apply cleanup (bomber crash) *before* checking win or ending turn
             const unitsAfterCleanup = handleEndOfTurnCleanup(OWNER_PLAYER, nextGameUnits);
             setGameUnits(unitsAfterCleanup); // Update state with move & cleanup result
             setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]);

              const winStatusChanged = checkWinCondition(unitsAfterCleanup); // Check win after move
              if (!winStatusChanged && gameStatus === STATUS_PLAYING) { // If game continues...
                  endPlayerTurn(unitsAfterCleanup); // ...start AI turn with the new state
              }
         }
     }, [gameUnits, clearedBushes, bushHexes, gameTurn, gameStatus, handleEndOfTurnCleanup, checkWinCondition, endPlayerTurn]);


     // --- Player Action: Attack Unit ---
     // Use useCallback as it's passed as prop or used in handleHexClick
     const attackUnit = useCallback((attackerHexId, targetHexId) => {
         const attackerUnitInfo = gameUnits[attackerHexId];
         const targetUnitInfo = gameUnits[targetHexId];

         // Validations
         if (!attackerUnitInfo || !targetUnitInfo || attackerUnitInfo.owner === targetUnitInfo.owner || targetUnitInfo.owner === OWNER_NATURE) {
              setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]); return;
         }
         // Attacking Banana Tree = Capture (use move logic)
         if (targetUnitInfo.type === 'BANANA_TREE') {
             moveUnit(attackerHexId, targetHexId); return;
         }

         const attackerStats = UNIT_TYPES[attackerUnitInfo.type];
         const targetStats = UNIT_TYPES[targetUnitInfo.type];
         const damage = Math.max(0, attackerStats.attack || 0);
         const newTargetHealth = Math.max(0, targetUnitInfo.health - damage);
         let attackMessage = "";
         const newGameUnits = JSON.parse(JSON.stringify(gameUnits)); // Copy state

         if (newTargetHealth <= 0) {
             attackMessage = `${attackerStats.name} (${attackerUnitInfo.owner}) defeated ${targetStats.name} (${targetUnitInfo.owner})!`;
             newGameUnits[targetHexId] = { ...attackerUnitInfo }; // Move attacker in
             delete newGameUnits[attackerHexId];
         } else {
             newGameUnits[targetHexId] = { ...targetUnitInfo, health: newTargetHealth }; // Update health
              attackMessage = `${attackerStats.name} (${attackerUnitInfo.owner}) dealt ${damage} damage to ${targetStats.name} (${targetUnitInfo.owner}).`;
         }

         setMessage(attackMessage);
         // Apply cleanup (bomber crash) *before* checking win or ending turn
         const unitsAfterCleanup = handleEndOfTurnCleanup(OWNER_PLAYER, newGameUnits);
         setGameUnits(unitsAfterCleanup); // Update state with attack & cleanup result
         setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]);

         const winStatusChanged = checkWinCondition(unitsAfterCleanup); // Check win after attack
          if (!winStatusChanged && gameStatus === STATUS_PLAYING) { // If game continues...
              endPlayerTurn(unitsAfterCleanup); // ...start AI turn
          }

     }, [gameUnits, gameStatus, moveUnit, handleEndOfTurnCleanup, checkWinCondition, endPlayerTurn]);

    // --- Player Action: Select Unit ---
     // useCallback recommended if passed down, good practice anyway
     const selectUnit = useCallback((hexId) => {
         const unit = gameUnits[hexId];
         if (!unit || unit.owner !== OWNER_PLAYER || currentTurn !== OWNER_PLAYER || gameStatus !== STATUS_PLAYING) {
            // Clear selection if invalid target clicked while trying to select
            setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]);
            setMessage("Player's turn - Select a unit");
            return;
         }

         setSelectedHex(hexId);
         const [col, row] = hexId.split(',').map(Number);
         const unitStats = UNIT_TYPES[unit.type];

         const calculatedMoves = unitStats.moveRange > 0 ? calculatePossibleMoves(col, row, unitStats.moveRange, unit.type) : [];
         setPossibleMoves(calculatedMoves);
         const calculatedAttacks = unitStats.attackRange > 0 ? calculatePossibleAttacks(col, row, unitStats.attackRange, unit.owner) : [];
         setPossibleAttacks(calculatedAttacks);

         // Update message based on actions
         let selectionMessage = `Selected ${unitStats.name}. `;
          if (calculatedMoves.length > 0 && calculatedAttacks.length > 0) selectionMessage += "Click tan hex to move or red hex to attack.";
          else if (calculatedMoves.length > 0) selectionMessage += "Click tan hex to move.";
          else if (calculatedAttacks.length > 0) selectionMessage += "Click red hex to attack.";
          else selectionMessage += "No available actions.";
          setMessage(selectionMessage);

     }, [gameUnits, currentTurn, gameStatus, calculatePossibleMoves, calculatePossibleAttacks]); // Dependencies


    // --- Player Action: Handle Hex Click ---
     // Needs access to many state vars and action functions
     // useCallback recommended
     const handleHexClick = useCallback((hex) => { // Receives { id, col, row, x, y }
         if (currentTurn !== OWNER_PLAYER || gameStatus !== STATUS_PLAYING) return;

         const hexId = hex.id; // Use hex.id ("col,row")
         const unitOnHex = gameUnits[hexId];

         if (selectedHex) {
             const selectedUnit = gameUnits[selectedHex];
             if (!selectedUnit) { // Safety check
                 setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]); setMessage("Selection error. Select a unit."); return;
             }

             if (possibleAttacks.includes(hexId)) { attackUnit(selectedHex, hexId); return; }
             if (possibleMoves.includes(hexId)) { moveUnit(selectedHex, hexId); return; }
             if (hexId === selectedHex) { // Deselect
                 setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]); setMessage("Player's turn - Select a unit"); return;
             }
             if (unitOnHex && unitOnHex.owner === OWNER_PLAYER) { // Switch selection
                 selectUnit(hexId); return;
             }
             // Clicked elsewhere - deselect
             setSelectedHex(null); setPossibleMoves([]); setPossibleAttacks([]); setMessage("Player's turn - Select a unit"); return;

         } else { // Nothing selected
             if (unitOnHex && unitOnHex.owner === OWNER_PLAYER) { // Select unit
                 selectUnit(hexId);
             }
             // Clicking empty/enemy hex does nothing when nothing selected
         }
     }, [currentTurn, gameStatus, selectedHex, possibleMoves, possibleAttacks, gameUnits, selectUnit, moveUnit, attackUnit]); // Dependencies

    // --- Render ---
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem', maxWidth: '100%' }}>
            <a className="back-to-hub" href="https://freeappstoenjoy.com" style={{ alignSelf: 'flex-start' }}>&larr; All Games and Apps</a>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>The Island of Wiggi Wiggi</h2>

            <GameInfoPanel message={message} gameStatus={gameStatus} gameTurn={gameTurn} />

            <Controls onReset={initializeGame} gameStatus={gameStatus} />

            <GameBoard
                gameUnits={gameUnits}
                selectedHex={selectedHex}
                possibleMoves={possibleMoves}
                possibleAttacks={possibleAttacks}
                clearedBushes={clearedBushes}
                bushHexes={bushHexes}
                visibilityData={visibilityData}
                onHexClick={handleHexClick}
                currentTurn={currentTurn}
                gameStatus={gameStatus}
            />

            <InstructionsPanel />
        </div>
    );
};

export default WiggiWiggiGame;